import { PartLabelDisplay } from "../models/LabelDisplay";
import { IPartTagTemplate } from "../models/PartTagTemplate";

export type PartTagFieldPath =
  | "tagNo"
  | "logo"
  | "date"
  | "qr"
  | "price"
  | "vehicleStack.make"
  | "vehicleStack.model"
  | "vehicleStack.vrm"
  | "vehicleStack.year"
  | "vehicleStack.engineCode"
  | "vehicleStack.vin"
  | "vehicleStack.fuelType"
  | "position"
  | "qc"
  | "imaged";

export type PartTagField = { path: PartTagFieldPath; label: string; hint: string };

export type PartTagFieldGroup = { title: string; description: string; fields: PartTagField[] };

// Grouped by where each field sits on the printed label, so the editor reads like the tag does.
// The line under the header is a single choice (see PART_TAG_LINE_MODES), not a checkbox.
export const PART_TAG_FIELD_GROUPS: PartTagFieldGroup[] = [
  {
    title: "Header",
    description: "The strip across the top of the tag.",
    fields: [
      { path: "date", label: "Created date", hint: "The date the part was created" },
      { path: "tagNo", label: "Tag number", hint: "The part's tag number" },
      { path: "logo", label: "Logo", hint: "Your company logo" },
    ],
  },
  {
    title: "Left column",
    description: "Scan and price.",
    fields: [
      { path: "qr", label: "QR code", hint: "Scans to the part" },
      { path: "price", label: "Price", hint: "Never printed on core part tags" },
    ],
  },
  {
    title: "Vehicle details",
    description: "The stack of vehicle facts in the middle of the tag.",
    fields: [
      { path: "vehicleStack.make", label: "Make", hint: "" },
      { path: "vehicleStack.model", label: "Model", hint: "" },
      { path: "vehicleStack.vrm", label: "Registration", hint: "The VRM" },
      { path: "vehicleStack.year", label: "Year", hint: "Year of manufacture" },
      { path: "vehicleStack.engineCode", label: "Engine code", hint: "" },
      { path: "vehicleStack.vin", label: "VIN", hint: "The verified VIN, or the vehicle's VIN if not yet verified" },
      { path: "vehicleStack.fuelType", label: "Fuel type", hint: "" },
    ],
  },
  {
    title: "Right column",
    description: "Warehouse checks.",
    fields: [
      { path: "position", label: "Location", hint: "The warehouse location" },
      { path: "qc", label: "QC", hint: "Whether the part has passed QC" },
      { path: "imaged", label: "Imaged", hint: "Whether the part has photos" },
    ],
  },
];

// The two flags the line-mode choice stands in for. They are deliberately not in the groups above.
export const PART_TAG_LINE_FLAGS = ["makeModelCategoryLine", "stockNumberLine"] as const;

export type PartTagLineMode = "makeModelCategory" | "stockNumber" | "none";

export const PART_TAG_LINE_MODES: { value: PartTagLineMode; label: string; hint: string }[] = [
  { value: "makeModelCategory", label: "Make, model and category", hint: "e.g. FORD FOCUS • Alternator" },
  { value: "stockNumber", label: "Stock number", hint: "The vehicle's stock number instead" },
  { value: "none", label: "Hide this line", hint: "Print nothing under the header" },
];

// Stock number wins if both flags are somehow set, matching the API and the label template.
export const lineModeFromDisplay = (
  display: Pick<PartLabelDisplay, "makeModelCategoryLine" | "stockNumberLine">
): PartTagLineMode => {
  if (display.stockNumberLine) return "stockNumber";
  if (display.makeModelCategoryLine) return "makeModelCategory";
  return "none";
};

export const applyLineMode = <T extends Pick<PartLabelDisplay, "makeModelCategoryLine" | "stockNumberLine">>(
  display: T,
  mode: PartTagLineMode
): T => ({
  ...display,
  makeModelCategoryLine: mode === "makeModelCategory",
  stockNumberLine: mode === "stockNumber",
});

export const partTagFieldPaths = (): PartTagFieldPath[] =>
  PART_TAG_FIELD_GROUPS.flatMap((g) => g.fields.map((f) => f.path));

// True when the path names a real flag on the display shape. Guards the field list against
// drifting away from PartLabelDisplay when either changes.
export const hasDisplayPath = (display: PartLabelDisplay, path: string): boolean => {
  const [head, tail] = path.split(".");
  if (tail === undefined) return typeof (display as Record<string, unknown>)[head] === "boolean";
  if (head !== "vehicleStack") return false;
  return typeof (display.vehicleStack as Record<string, unknown>)[tail] === "boolean";
};

// What the editor's preview prints. Representative values, not a real part.
export const samplePartLabelProps = {
  tagNo: "AB12345",
  date: "01 / 01 / 2026",
  make: "FORD",
  model: "FOCUS",
  category: "Alternator",
  stockNumber: "STK-00123",
  accountName: "",
  vrm: "AB12 CDE",
  year: "2018",
  engineCode: "M1DA",
  vin: "WF0KXXGCBKJA12345",
  fuelType: "Petrol",
  price: "£45.00",
  position: "A1-02",
  qc: "QC",
  imaged: "Img",
};

// Which tag currently owns each category, keyed by category id.
export const categoryOwnerMap = (tags: IPartTagTemplate[] | undefined): Record<string, { tagId: string; name: string }> => {
  const map: Record<string, { tagId: string; name: string }> = {};
  (tags ?? []).forEach((tag) => {
    (tag.categoryIds ?? []).forEach((categoryId) => {
      map[categoryId] = { tagId: tag.id, name: tag.name };
    });
  });
  return map;
};

// React Hook Form hands number inputs back as strings.
export type PartTagFormDisplay = Omit<PartLabelDisplay, "widthCm" | "heightCm"> & {
  widthCm?: number | string;
  heightCm?: number | string;
};

export type PartTagFormValues = { name: string; lineMode: PartTagLineMode; display: PartTagFormDisplay };

const toPositiveNumber = (value: number | string | undefined): number | null => {
  if (value === undefined || value === null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

// Coerces the form's display into what the API accepts; null when the size is not usable.
// The line mode, when given, overrides the two line flags.
export const toDisplayRequest = (display: PartTagFormDisplay, lineMode?: PartTagLineMode): PartLabelDisplay | null => {
  const widthCm = toPositiveNumber(display.widthCm);
  const heightCm = toPositiveNumber(display.heightCm);
  if (widthCm === null || heightCm === null) return null;

  const result: PartLabelDisplay = {
    ...display,
    widthCm,
    heightCm,
    vehicleStack: { ...display.vehicleStack },
  };

  return lineMode ? applyLineMode(result, lineMode) : result;
};
